/**
 * SelectableOptionCard.tsx — the population / delivery-format / next-action card used across
 * the VER-128 Create Programme preview. One primitive for all three so selection styling
 * (border, tint, check mark) stays consistent instead of being re-implemented per step.
 */
import React from "react";
import { Box, ButtonBase, Chip, Stack, SvgIconTypeMap, Typography } from "@mui/material";
import { OverridableComponent } from "@mui/material/OverridableComponent";
import CheckCircleIcon from "@mui/icons-material/CheckCircle";
import { verbalTheme } from "../../layout/themes/verbal.theme";

export type SelectableOptionCardProps = {
    title: string;
    /** Small qualifier under the title, e.g. "Aged 4–10". Omit for cards with no subtitle. */
    subtitle?: string;
    body: string;
    icon: OverridableComponent<SvgIconTypeMap>;
    selected: boolean;
    onClick: () => void;
    /** Chip text shown under the body — used by the delivery-format cards. */
    tag?: string;
    tagColor?: "success" | "default" | "warning";
    /** Icon renders in a rounded tile instead of bare — used by delivery-format cards. */
    iconInTile?: boolean;
};

export const SelectableOptionCard: React.FC<SelectableOptionCardProps> = ({
    title,
    subtitle,
    body,
    icon: Icon,
    selected,
    onClick,
    tag,
    tagColor = "default",
    iconInTile = false,
}) => {
    return (
        <ButtonBase
            onClick={onClick}
            focusRipple
            sx={{
                position: "relative",
                display: "flex",
                alignItems: iconInTile ? "flex-start" : "stretch",
                gap: 2,
                p: 2.25,
                textAlign: "left",
                borderRadius: 1.5,
                border: "1.5px solid",
                borderColor: selected ? "primary.main" : "borderShade.main",
                backgroundColor: selected ? "primary.light" : "background.paper",
                transition: "background-color 160ms ease, border-color 160ms ease",
                width: "100%",
                height: "100%",
            }}
        >
            {iconInTile ? (
                <Box
                    flex="0 0 auto"
                    width={44}
                    height={44}
                    borderRadius={1.25}
                    display="flex"
                    alignItems="center"
                    justifyContent="center"
                    bgcolor={selected ? "background.paper" : "secondaryShade.main"}
                >
                    <Icon sx={{ fontSize: 24, color: "secondary.main" }} />
                </Box>
            ) : (
                <Icon sx={{ fontSize: 26, color: selected ? "secondary.dark" : "secondary.main", flex: "0 0 auto" }} />
            )}
            <Stack spacing={1} minWidth={0} flex="1 1 auto">
                <Stack spacing={0.25}>
                    <Typography variant="subtitle1" color="secondary.main">
                        {title}
                    </Typography>
                    {subtitle && (
                        <Typography variant="body2" color="text.secondary">
                            {subtitle}
                        </Typography>
                    )}
                </Stack>
                <Typography variant="body2" color="secondary.dark" sx={{ lineHeight: 1.55 }}>
                    {body}
                </Typography>
                {tag && (
                    <Chip
                        label={tag}
                        size="small"
                        color={tagColor}
                        sx={{ alignSelf: "flex-start", textTransform: "none" }}
                    />
                )}
            </Stack>
            <CheckCircleIcon
                sx={{
                    position: "absolute",
                    top: verbalTheme.spacing(1.25),
                    right: verbalTheme.spacing(1.25),
                    fontSize: 22,
                    color: "primary.main",
                    opacity: selected ? 1 : 0,
                    transition: "opacity 160ms ease",
                }}
            />
        </ButtonBase>
    );
};
